<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
        md="8"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableActivities.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
        md="4"
      >
        <BFormGroup
          label="Activity Type"
          label-for="filterActivityType"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormSelect
            id="filterActivityType"
            v-model="tableActivities.filter.activity_type"
            :options="tableActivities.options.activityTypes"
          >
            <template #first>
              <BFormSelectOption
                value=""
                disabled
                v-text="' - - filter here - - '"
              />
            </template>
          </BFormSelect>
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
      >
        <BButton
          size="sm"
          variant="primary"
          @click="createActivity"
        >
          Create Activity
        </BButton>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableActivities"
      :provider="tableProvider"
      :fields="tableActivities.fields"
      :filter="tableActivities.filter"
      :filter-on="tableActivities.filterOn"
      :sort-by="tableActivities.sortBy"
      :sort-desc="tableActivities.sortDesc"
      :sort-direction="tableActivities.sortDirection"
      :total-rows="tableActivities.totalRows"
      :current-page="tableActivities.currentPage"
      :per-page="tableActivities.perPage"
      :stacked="tableActivities.stacked"
      :busy="tableActivities.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableActivities.busy"
            @click.prevent="editActivity(item)"
          >
            Edit
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalActivity"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="MActivityTitle"
    >
      <ValidationObserver
        ref="formActivity"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="activity_type"
                name="activity type"
                rules="required"
              >
                <BFormGroup
                  label="Activity Type"
                  label-for="fActivityType"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.activityType"
                    input-id="fActivityType"
                    placeholder="select activity type here"
                    label="name"
                    clearable
                    append-to-body
                    :options="(options.activityTypes)"
                    :loading="(fetching.activityTypes)"
                    :disabled="(state.busy)"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                  >
                    <template #option="{ name }">
                      <div
                        class="text-break text-wrap"
                        v-text="name"
                      />
                    </template>
                    <template #selected-option="{ name }">
                      <div
                        class="w-200px w-md-300px text-truncate"
                        v-text="name"
                      />
                    </template>
                    <template #no-options="">
                      no available activity types
                    </template>
                  </VSelect>
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="activity code"
                vid="activity_code"
                rules="max:30"
              >
                <BFormGroup
                  label="Activity Code"
                  label-for="fActivityCode"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fActivityCode"
                    v-model="formActivity.activity_code"
                    type="text"
                    maxlength="30"
                    autocomplete="off"
                    placeholder="enter code here"
                    :state="errors.length > 0 ? false : null"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="activity_name"
                vid="activity_name"
                rules="required|max:100"
              >
                <BFormGroup
                  label="Activity Name"
                  label-for="fActivityName"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fActivityName"
                    v-model="formActivity.activity_name"
                    type="text"
                    maxlength="100"
                    autocomplete="off"
                    placeholder="enter activity name here"
                    :state="errors.length > 0 ? false : null"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="customer_per_event"
                name="customer per event"
                rules="required"
              >
                <BFormGroup
                  label="Customer Per Event"
                  label-for="fCustomerPerEvent"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fCustomerPerEvent"
                    v-model="formActivity.customer_per_event"
                    type="number"
                    step="1"
                    placeholder="enter per event here"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="landholding_per_customer"
                name="landholding per customer"
                rules="required"
              >
                <BFormGroup
                  label="Landholding Per Customer"
                  label-for="fLandholdingPerCustomer"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fLandholdingPerCustomer"
                    v-model="formActivity.landholding_per_customer"
                    type="number"
                    step="0.01"
                    placeholder="enter per customer here"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="target_hectare"
                name="target hectare"
                rules="required"
              >
                <BFormGroup
                  label="Target Hectare"
                  label-for="fTargetHectare"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fTargetHectare"
                    v-model="formActivity.target_hectare"
                    type="number"
                    step="0.01"
                    placeholder="enter target hectare here"
                    :state="errors.length > 0 ? false : null"
                    readonly
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="6"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="sales_per_event"
                name="sales per event"
                rules="required"
              >
                <BFormGroup
                  label="Sales Per Event"
                  label-for="fSalesPerEvent"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fSalesPerEvent"
                    v-model="formActivity.sales_per_event"
                    type="number"
                    step="0.01"
                    placeholder="enter per event here"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="6"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="budget_per_event"
                name="budget per event"
                rules="required"
              >
                <BFormGroup
                  label="Budget Per Event"
                  label-for="fBudgetPerEvent"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fBudgetPerEvent"
                    v-model="formActivity.budget_per_event"
                    type="number"
                    step="0.01"
                    placeholder="enter per event here"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              v-if="state.editing"
              cols="12"
            >
              <BFormGroup>
                <BFormCheckbox
                  id="fActive"
                  v-model="formActivity.active"
                  switch
                  value="1"
                  name="active"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>

        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateActivity"
        >
          {{ state.editing ? 'Update' : 'Create' }} Activity
        </BButton>
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>

    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import QUERY from 'lodash'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import { SAdminActivity } from '@/services/admin'

export default {
  name: 'VAdminActivities',

  mixins: [
    MxMisc,
    MxFormatter
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Activities'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      fetching: {
        activityTypes: false
      },
      selected: {
        activityType: ''
      },
      options: {
        activityTypes: [
          { id: 'Interaction', name: 'Interaction' },
          { id: 'Event', name: 'Event' }
        ]
      },
      formActivity: {
        id: '',
        activity_type: '',
        activity_code: '',
        activity_name: '',
        event_plan: '',
        customer_per_event: '',
        landholding_per_customer: '',
        target_hectare: '',
        sales_per_event: '',
        budget_per_event: '',
        active: 1
      },
      tableActivities: {
        busy: false,
        options: {
          activityTypes: [
            'All',
            'Interaction',
            'Event'
          ]
        },
        filter: {
          search: '',
          activity_type: 'All'
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 11, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 6, key: 'created_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 1, key: 'activity_type', class: 'min-w-150px' },
          { mobile: 2, key: 'activity_code', class: 'min-w-150px' },
          { mobile: 3, key: 'activity_name', class: 'min-w-175px' },
          { mobile: 4, key: 'customer_per_event', class: 'text-right min-w-md-75px', formatter: this.numberDefaultFormatter },
          { mobile: 5, key: 'landholding_per_customer', class: 'text-right min-w-md-75px', formatter: this.numberDefaultFormatter },
          { mobile: 6, key: 'target_hectare', class: 'text-right min-w-md-75px', formatter: this.numberDefaultFormatter },
          { mobile: 7, key: 'sales_per_event', class: 'text-right min-w-md-75px', formatter: this.numberDefaultFormatter },
          { mobile: 8, key: 'budget_per_event', class: 'text-right min-w-md-75px', formatter: this.numberDefaultFormatter },
          { mobile: 9, key: 'active', class: 'text-right w-md-75px', formatter: this.yesOrNo },
          { mobile: 10, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: Object.assign({
    MActivityTitle () {
      return this.state.editing ? 'Edit Activity' : 'Create Activity'
    }
  }, mapGetters({
    currentUser: 'auth/user'
  })),

  watch: {
    'selected.activityType' (activityType) {
      this.formActivity.activity_type = activityType?.id || ''
    },

    'formActivity.landholding_per_customer' (landholdingPerCustomer) {
      this.formActivity.target_hectare = Number(landholdingPerCustomer || 0) * Number(this.formActivity.customer_per_event || 0)
    },

    'formActivity.customer_per_event' (customerPerEvent) {
      this.formActivity.target_hectare = Number(customerPerEvent || 0) * Number(this.formActivity.landholding_per_customer || 0)
    }
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableActivities.busy = true
      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        activity_type: filter.activity_type
      }

      return await SAdminActivity.get(payload).then(
        ({ data }) => {
          this.tableActivities.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableActivities.totalRows = 0
        return []
      }).finally(() => {
        this.tableActivities.busy = false
      })
    },

    createActivity () {
      this.state.editing = false
      this.selected.activityType = null

      this.formActivity = Object.assign(this.formActivity, {
        id: '',
        activity_type: '',
        activity_code: '',
        activity_name: '',
        event_plan: '',
        customer_per_event: '',
        landholding_per_customer: '',
        target_hectare: '',
        sales_per_event: '',
        budget_per_event: '',
        active: 1
      })

      this.$refs.modalActivity.show()
    },

    editActivity (activity) {
      this.state.editing = true
      this.formActivity = Object.assign(this.formActivity, {
        id: activity.id,
        activity_type: activity.activity_type,
        activity_code: activity.activity_code,
        activity_name: activity.activity_name,
        event_plan: activity.event_plan,
        customer_per_event: activity.customer_per_event,
        landholding_per_customer: activity.landholding_per_customer,
        target_hectare: activity.target_hectare,
        sales_per_event: activity.sales_per_event,
        budget_per_event: activity.budget_per_event,
        active: activity.active
      })

      this.selected.activityType = QUERY.find(this.options.activityTypes, {
        id: activity.activity_type
      })

      this.$refs.modalActivity.show()
    },

    async validateActivity () {
      await this.$refs.formActivity.validate().then(
        async validActivity => {
          if (!validActivity) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Activity?</h6>' : '<h6>Create this Activity?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putActivity() : this.postActivity()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  this.$refs.formActivity.setErrors(message.errors)
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postActivity () {
      return await SAdminActivity.post(this.formActivity).then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalActivity.hide()
            this.$refs.tableActivities.refetch()
          })
        }
      )
    },

    async putActivity () {
      return await SAdminActivity.put(this.formActivity).then(
        ({ data: { message, activity } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalActivity.hide()
            this.$refs.tableActivities.updateRow({
              id: activity.id,
              activity_type: activity.activity_type,
              activity_code: activity.activity_code,
              activity_name: activity.activity_name,
              active: activity.active,
              updated_at: activity.updated_at
            })
          })
        }
      )
    }
  }
}
</script>
